/**
 * Calcula el estado automático de un cliente según sus viajes.
 * 
 * Estados:
 * - sin_viaje:  No tiene viajes registrados o todos cancelados
 * - pendiente:  Tiene viaje futuro, faltan más de 2 días (48hs)
 * - checkin:    Faltan 0-2 días para la salida O el día de la salida mismo
 * - en_viaje:   Hoy está entre fecha_salida y fecha_regreso
 * - completado: Todos sus viajes ya pasaron
 */
export function calcularEstadoCliente(viajes) {
  const hoy = new Date()
  hoy.setHours(12, 0, 0, 0) // Mediodía para evitar problemas de zona horaria

  if (!viajes || viajes.length === 0) {
    return { estado: "sin_viaje", label: "Sin viaje", color: "#6b7280", viaje: null }
  }

  // Excluir cancelados
  const activos = viajes.filter(v => {
    const e = (v.estado || "").toLowerCase()
    return e !== "cancelado"
  })

  if (activos.length === 0) {
    return { estado: "sin_viaje", label: "Sin viaje", color: "#6b7280", viaje: null }
  }

  // ── 1. Buscar si hay viaje en curso ahora mismo ──
  const enCurso = activos.find(v => {
    if (!v.fecha_salida) return false
    const salida = parsearFecha(v.fecha_salida)
    if (!salida) return false

    // Si tiene fecha_regreso la usamos; si no, asumimos que sigue en viaje
    if (v.fecha_regreso) {
      const regreso = parsearFecha(v.fecha_regreso)
      if (!regreso) return false
      regreso.setHours(23, 59, 0, 0)
      return hoy >= salida && hoy <= regreso
    }

    // Sin fecha_regreso: está en viaje si hoy es >= salida y <= salida + 30 días
    const limite = new Date(salida)
    limite.setDate(limite.getDate() + 30)
    return hoy >= salida && hoy <= limite
  })

  if (enCurso) {
    return { estado: "en_viaje", label: "En viaje", color: "#10b981", viaje: enCurso }
  }

  // ── 2. Buscar próximo viaje futuro ──
  const futuros = activos
    .filter(v => {
      if (!v.fecha_salida) return false
      const salida = parsearFecha(v.fecha_salida)
      if (!salida) return false
      return salida >= hoy
    })
    .sort((a, b) => {
      const sa = parsearFecha(a.fecha_salida)
      const sb = parsearFecha(b.fecha_salida)
      return sa - sb
    })

  if (futuros.length > 0) {
    const proximo = futuros[0]
    const salida  = parsearFecha(proximo.fecha_salida)
    const diffMs  = salida - hoy
    const diffHoras = diffMs / (1000 * 60 * 60)

    // Check-in: dentro de las próximas 48 horas
    if (diffHoras <= 48) {
      return { estado: "checkin", label: "Check-in", color: "#f59e0b", viaje: proximo }
    }

    return { estado: "pendiente", label: "Pendiente", color: "#3b82f6", viaje: proximo }
  }

  // ── 3. Todos los viajes ya pasaron ──
  return { estado: "completado", label: "Completado", color: "#8b5cf6", viaje: null }
}

/**
 * Parsea una fecha en formato YYYY-MM-DD o DD/MM/YYYY
 * Devuelve un objeto Date o null si no puede parsear
 */
function parsearFecha(str) {
  if (!str) return null
  const s = str.trim()

  // Formato YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number)
    const fecha = new Date(y, m - 1, d, 0, 0, 0, 0)
    return isNaN(fecha) ? null : fecha
  }

  // Formato DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split("/").map(Number)
    const fecha = new Date(y, m - 1, d, 0, 0, 0, 0)
    return isNaN(fecha) ? null : fecha
  }

  // Intentar parseo nativo como último recurso
  const fecha = new Date(s)
  return isNaN(fecha) ? null : fecha
}

export const COLORES_ESTADO = {
  sin_viaje:  { bg: "#1a1a1a",  text: "#6b7280"  },
  pendiente:  { bg: "#1e3a5f",  text: "#3b82f6"  },
  checkin:    { bg: "#3a2a0a",  text: "#f59e0b"  },
  en_viaje:   { bg: "#0a2a1a",  text: "#10b981"  },
  completado: { bg: "#2a1a3a",  text: "#8b5cf6"  },
}